import { CircularProgress, Typography, Box, Stack, Alert } from "@mui/material";
import axios from "axios";
import { useState } from "react";
import AdminNavigation from "Navigation/AdminNavigation";
import Layout from "../../../Components/Layout/Layout";
import useAxiosEffect from "../../../system/useAxiosEffect";
import Search from "../../../Components/Search";
import { useHistory } from "react-router-dom";
import ListItem from "../../../Components/ListItem";
import ListItemContent from "../../../Components/ListItemContent";
import ListItemActions from "../../../Components/ListItemActions";
import ListItemAction from "../../../Components/ListItemAction";
import ListItemChip from "../../../Components/ListItemChip";
import ListItemChips from "../../../Components/ListItemChips";
import Check from "@mui/icons-material/Check";
import Clear from "@mui/icons-material/Clear";
import SendIcon from "@mui/icons-material/Send";
import Launch from "@mui/icons-material/Launch";
import EventBusy from "@mui/icons-material/EventBusy";
import { Job, ThemeColor } from "../../../system/Domain";
import { formatJobStatus, paintJobStatus } from "./JobListItem";
import JobHistory from "./JobHistory";
import formatDateTimeAldTimezone from "../../../system/formatDateTimeAldTimezone";

export interface CronJobMetadata {
	key: string;
	jobType?: string;
	method: string;
	cronExpression: string;
	registered: boolean;
	job?: Job | null;
}

interface TriggerResult {
	id: string;
	created: boolean;
}

interface UnregisterResult {
	unregistered: boolean;
}

const CronHub = () => {
	type Mode = "view" | "trigger-confirm" | "trigger-result" | "unregister-confirm" | "unregister-result";

	const [entries, setEntries] = useState<CronJobMetadata[]>();
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [modes, setModes] = useState<Record<string, Mode>>({});
	const [loading, setLoading] = useState<Record<string, boolean>>({});
	const [error, setError] = useState<string | null>(null);
	const [triggerResults, setTriggerResults] = useState<Record<string, TriggerResult>>({});
	const [unregisterResults, setUnregisterResults] = useState<Record<string, UnregisterResult>>({});
	const history = useHistory();

	const getMode = (key: string): Mode => modes[key] || "view";
	const setMode = (key: string, mode: Mode) => {
		setModes((prev) => ({ ...prev, [key]: mode }));
	};

	const [, reload] = useAxiosEffect(async (config) => {
		const { data } = await axios.get<CronJobMetadata[]>(`/api/admin/jobs/cron/metadata`, config);
		setEntries(data);
	}, []);

	const filteredEntries = entries?.filter(
		(e) =>
			e.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
			e.jobType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			e.cronExpression.toLowerCase().includes(searchTerm.toLowerCase()),
	) || [];

	const getModeColor = (entry: CronJobMetadata, mode: Mode): ThemeColor => {
		if (mode === "trigger-confirm" || mode === "unregister-confirm") {
			return "primary";
		}
		if (mode === "trigger-result") {
			return "success";
		}
		if (mode === "unregister-result") {
			return "warning";
		}
		if (!entry.registered) {
			return "warning";
		}
		return entry.job ? paintJobStatus(entry.job.status) : "info";
	};

	const handleTrigger = async (entry: CronJobMetadata) => {
		setLoading((prev) => ({ ...prev, [entry.key]: true }));
		setError(null);

		try {
			const { data } = await axios.post<TriggerResult>(`/api/admin/jobs/cron/${entry.key}`);

			setTriggerResults((prev) => ({ ...prev, [entry.key]: data }));
			setMode(entry.key, "trigger-result");

			await reload();
		} catch (err: any) {
			setError(err.response?.data?.message || err.message || "Failed to trigger cron job");
		} finally {
			setLoading((prev) => ({ ...prev, [entry.key]: false }));
		}
	};

	const handleUnregister = async (entry: CronJobMetadata) => {
		setLoading((prev) => ({ ...prev, [entry.key]: true }));
		setError(null);

		try {
			const { data } = await axios.post<UnregisterResult>(`/api/admin/jobs/cron/${entry.key}/unregister`);

			setUnregisterResults((prev) => ({ ...prev, [entry.key]: data }));
			setMode(entry.key, "unregister-result");

			await reload();
		} catch (err: any) {
			setError(err.response?.data?.message || err.message || "Failed to unregister cron job");
		} finally {
			setLoading((prev) => ({ ...prev, [entry.key]: false }));
		}
	};

	const [historyOpen, setHistoryOpen] = useState<Record<string, boolean>>({});
	const toggleHistory = (key: string) => setHistoryOpen((prev) => ({ ...prev, [key]: !prev[key] }));

	return (
		<Layout title="Cron Hub" navigation={<AdminNavigation />}>
			<Box mb={2}>
				<Search
					value={searchTerm}
					placeholder="Search cron jobs..."
					realtime
					onSearch={(value) => setSearchTerm(value)}
				/>
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
					{error}
				</Alert>
			)}

			{!entries && <CircularProgress size={48} />}
			{entries && (
				<Stack spacing={2}>
					<Typography variant="body2" color="textSecondary">
						Found {filteredEntries.length} cron job{filteredEntries.length !== 1 ? "s" : ""}
					</Typography>
					{filteredEntries.map((entry) => {
						const mode = getMode(entry.key);
						const isLoading = loading[entry.key] || false;
						const color = getModeColor(entry, mode);
						const triggerResult = triggerResults[entry.key];
						const unregisterResult = unregisterResults[entry.key];

						return (
							<ListItem key={entry.key} color={color}>
								<ListItemActions color={color} disabled={isLoading}>
									{mode === "view" && (
										<>
											<ListItemAction
												icon={<SendIcon />}
												tooltip="Terminierung anstoßen"
												onClick={() => setMode(entry.key, "trigger-confirm")}
											/>
											{entry.registered && (
												<ListItemAction
													icon={<EventBusy />}
													tooltip="Registrierung aufheben"
													onClick={() => setMode(entry.key, "unregister-confirm")}
												/>
											)}
										</>
									)}
									{mode === "trigger-confirm" && (
										<>
											<ListItemAction
												icon={<Check />}
												tooltip="Terminierung bestätigen"
												disabled={isLoading}
												onClick={() => handleTrigger(entry)}
											/>
											<ListItemAction
												icon={<Clear />}
												tooltip="Abbrechen"
												disabled={isLoading}
												onClick={() => setMode(entry.key, "view")}
											/>
										</>
									)}
									{mode === "unregister-confirm" && (
										<>
											<ListItemAction
												icon={<Check />}
												tooltip="Aufhebung bestätigen"
												disabled={isLoading}
												onClick={() => handleUnregister(entry)}
											/>
											<ListItemAction
												icon={<Clear />}
												tooltip="Abbrechen"
												disabled={isLoading}
												onClick={() => setMode(entry.key, "view")}
											/>
										</>
									)}
									{(mode === "trigger-result" || mode === "unregister-result") && (
										<>
											{mode === "trigger-result" && triggerResult?.id && (
												<ListItemAction
													icon={<Launch />}
													tooltip="Job öffnen"
													onClick={() => history.push(`/admin/jobs/${triggerResult.id}`)}
												/>
											)}
											<ListItemAction
												icon={<Clear />}
												tooltip="Schließen"
												onClick={() => setMode(entry.key, "view")}
											/>
										</>
									)}
									{mode === "view" && entry.job && (
										<ListItemAction
											icon={<Launch />}
											tooltip="Job-Details öffnen"
											onClick={() => history.push(`/admin/jobs/${entry.job.id}`)}
										/>
									)}
								</ListItemActions>
								<ListItemContent color={color}>
									<Typography variant="h6" gutterBottom>
										{entry.jobType || entry.key}
									</Typography>
									<Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: "block" }}>
										Key: <code>{entry.key}</code> · Methode: <code>{entry.method}</code>
									</Typography>

									<ListItemChips>
										{entry.registered ? (
											<ListItemChip label="Registriert" color="success" />
										) : (
											<ListItemChip label="Nicht registriert" color="warning" />
										)}
										<ListItemChip label={<code>{entry.cronExpression}</code>} color="info" />
										{entry.job && (
											<ListItemChip
												label={formatJobStatus(entry.job.status)}
												color={paintJobStatus(entry.job.status)}
												active={!!historyOpen[entry.key]}
												tooltip="Verlauf ein-/ausblenden"
												onClick={() => toggleHistory(entry.key)}
											/>
										)}
										{entry.job?.dateScheduled && (
											<ListItemChip label={`Nächste Ausführung: ${formatDateTimeAldTimezone(entry.job.dateScheduled)}`} />
										)}
									</ListItemChips>

									{mode === "unregister-confirm" && (
										<Box sx={{ mt: 2 }}>
											<Alert severity="warning">
												Soll die Registrierung dieses CronJobs wirklich aufgehoben werden? Er wird dann nicht
												mehr automatisch terminiert. Die Job-Historie bleibt erhalten und er kann jederzeit wieder
												aktiviert werden.
											</Alert>
										</Box>
									)}

									{mode === "trigger-result" && triggerResult && (
										<Box sx={{ mt: 2 }}>
											<Alert severity="success">
												Cronjob wurde {triggerResult.created ? "neu registriert" : "aktualisiert"}. Job-ID:{" "}
												<strong>{triggerResult.id}</strong>
											</Alert>
										</Box>
									)}

									{mode === "unregister-result" && unregisterResult && (
										<Box sx={{ mt: 2 }}>
											<Alert severity={unregisterResult.unregistered ? "success" : "warning"}>
												{unregisterResult.unregistered
													? "Registrierung wurde aufgehoben."
													: "CronJob war nicht registriert, es gab nichts aufzuheben."}
											</Alert>
										</Box>
									)}

									{!entry.job && mode === "view" && (
										<Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
											Dieser CronJob wurde noch nicht registriert. Über den Button links kann er jetzt terminiert
											werden.
										</Typography>
									)}

									{entry.job && historyOpen[entry.key] && (
										<Box sx={{ mt: 2 }}>
											<JobHistory job={entry.job} />
										</Box>
									)}
								</ListItemContent>
							</ListItem>
						);
					})}
				</Stack>
			)}
		</Layout>
	);
};

export default CronHub;
